// Ability UI rendering module

import { abilityManager } from './abilities.ts';
import type { AbilityManager, ClientAbilityState } from './abilities.ts';
import { getAbilities } from './game.ts';
import { cellLabel } from './gridUtils.ts';
import type { GameState } from './game.ts';
import type { Axis, Coord, RevealedCell, RowCol } from '../../shared/abilityLogic.ts';
import type { AbilityResult, BuffStateWire, OpponentAbility, PassiveResult } from '../../shared/protocol.ts';
import type { renderer as rendererInstance } from './renderer.ts';

type Renderer = typeof rendererInstance;

// ─── Notification payload slices ─────────────────────────────────
// The renderer only reads a few fields off the (much larger) wire payloads;
// these structural slices accept AbilityResult / PassiveEventResult / ad-hoc
// notification objects alike.

/** Slice of AbilityResult rendered by showAbilityResult. */
export interface AbilityResultNotice {
    success: boolean;
    message: string;
    abilityName?: string;
}

/** Slice of PassiveResult / TurnStartMechanicResult rendered by showPassiveTriggered. */
export interface PassiveNotice {
    abilityId?: string;
    abilityName?: string;
    message?: string;
}

/** Numeric ability params consumed by the dynamic description generators. */
type NumericParams = Record<string, number>;

// Dynamic description generators keyed by ability ID
// Returns a human-readable description reflecting current (potentially upgraded) params
const DYNAMIC_DESCRIPTIONS: Record<string, (p: NumericParams) => string> = {
    area_reveal: (p) => `Reveal all cells in a ${p.revealSize}x${p.revealSize} area`,
    multi_shot: (p) => `Fire ${p.extraShots + 1} shots this turn instead of one`,
    repair: (p) => `Restore ${p.cellsPerUse} previously hit cell${p.cellsPerUse > 1 ? 's' : ''} on your ship`,
    scrap_salvo: (p) => `Fire ${p.shotCount} shots at random unhit cells`,
    orbital_bombardment: (p) => `Scan row/column and auto-hit ${p.maxHits > 1 ? `up to ${p.maxHits}` : 'first'} ship cell${p.maxHits > 1 ? 's' : ''}`,
    deflect: (p) => `${Math.round(p.deflectChance * 100)}% chance to negate an incoming hit`,
    splash_damage: (p) => `After losing a ship, your next shot hits ${p.splashRadius > 1 ? `within radius ${p.splashRadius}` : 'adjacent cells'} too`,
    relentless_assault: (p) => `When you score a hit, immediately take ${p.bonusShots} bonus shot${p.bonusShots > 1 ? 's' : ''}`,
    guerrilla_tactics: (p) => `${Math.round(p.triggerChance * 100)}% chance for bonus shot when enemy misses`,
    probe_network: (p) => `Place a sensor probe that detects ships in ${p.detectionRadius}x${p.detectionRadius} area for ${p.duration} turns`,
    hunter_killer_mine: (p) => `Deploy mine with ${p.seekRadiusWithoutProbe}x${p.seekRadiusWithoutProbe} seek (${p.seekRadiusWithProbe}x${p.seekRadiusWithProbe} with probe)`,
    damage_control: (p) => `${Math.round(p.healChance * 100)}% chance each turn to restore ${p.cellsPerTrigger} damaged cell${p.cellsPerTrigger > 1 ? 's' : ''}`,
};

// Check if an ability's params have been upgraded from base values
function isAbilityUpgraded(ability: ClientAbilityState): boolean {
    const baseAbilities = getAbilities();
    const base = baseAbilities[ability.id];
    if (!base || !base.params || !ability.params) return false;

    for (const [key, val] of Object.entries(ability.params)) {
        if (base.params[key] !== undefined && val !== base.params[key]) {
            return true;
        }
    }
    return false;
}

// Get a dynamic description if the ability has been upgraded, otherwise null
function getUpgradedDescription(ability: ClientAbilityState): string | null {
    const generator = DYNAMIC_DESCRIPTIONS[ability.id];
    if (!generator || !ability.params) return null;
    if (!isAbilityUpgraded(ability)) return null;
    return generator(ability.params as NumericParams);
}

const ABILITY_ICONS: Record<string, string> = {
    'radar': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/><line x1="12" y1="2" x2="12" y2="6"/></svg>',
    'double-arrow': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M7 8l5-5 5 5"/><path d="M7 16l5 5 5-5"/><line x1="12" y1="3" x2="12" y2="21"/></svg>',
    'shield': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    'explosion': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"/></svg>',
    'wave': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12c2-4 4-4 6 0s4 4 6 0 4-4 6 0 4 4 6 0"/></svg>',
    'scanner': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/><path d="M11 8v6"/><path d="M8 11h6"/></svg>',
    'relentless': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
    'repair': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    'decoy': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke-dasharray="4 2"/><circle cx="12" cy="12" r="4" stroke-dasharray="2 2"/></svg>',
    'burst': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>',
    'drone': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="7" y="9" width="10" height="6" rx="1"/><line x1="4" y1="9" x2="7" y2="9"/><line x1="17" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="7" y2="15"/><line x1="17" y1="15" x2="20" y2="15"/><circle cx="4" cy="9" r="2"/><circle cx="20" cy="9" r="2"/><circle cx="4" cy="15" r="2"/><circle cx="20" cy="15" r="2"/><line x1="12" y1="15" x2="12" y2="20"/><polygon points="10,20 12,23 14,20"/></svg>',
    'ambush': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="1" fill="currentColor"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="22"/><line x1="2" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="22" y2="12"/></svg>',
    'orbital': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L6 8h4v5H6l6 6 6-6h-4V8h4L12 2z"/><line x1="4" y1="20" x2="20" y2="20" stroke-dasharray="3 2"/></svg>',
    'eye': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
    'power': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/><line x1="12" y1="2" x2="12" y2="5"/><line x1="5" y1="5" x2="7" y2="7"/><line x1="19" y1="5" x2="17" y2="7"/></svg>',
    'probe': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="4"/><circle cx="12" cy="12" r="9" stroke-dasharray="3 2"/><line x1="12" y1="3" x2="12" y2="1"/><line x1="12" y1="23" x2="12" y2="21"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><circle cx="12" cy="12" r="1" fill="currentColor"/></svg>',
    'mine': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="6"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="5.6" y1="5.6" x2="8.1" y2="8.1"/><line x1="15.9" y1="15.9" x2="18.4" y2="18.4"/><line x1="5.6" y1="18.4" x2="8.1" y2="15.9"/><line x1="15.9" y1="8.1" x2="18.4" y2="5.6"/><circle cx="12" cy="12" r="2" fill="currentColor"/></svg>',
    'wrench': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    'default': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>'
};

// Helper to safely render icon SVG from hardcoded ABILITY_ICONS map
function renderIcon(container: HTMLElement, iconKey: string): void {
    const iconSvg = ABILITY_ICONS[iconKey] || ABILITY_ICONS['default'];
    // Parse SVG string safely using DOMParser - icons are from hardcoded constant
    const parser = new DOMParser();
    const doc = parser.parseFromString(iconSvg, 'image/svg+xml');
    const svgEl = doc.documentElement;
    if (svgEl && svgEl.nodeName === 'svg') {
        container.appendChild(svgEl);
    }
}

// Reading offsetHeight forces a synchronous reflow so a re-added animation
// class restarts its animation.
function forceReflow(el: HTMLElement): number {
    return el.offsetHeight;
}

class AbilityRenderer {
    declare panelElement: HTMLDivElement | null;
    declare buffElement: HTMLDivElement | null;
    declare onAbilityClick: ((abilityId: string, immediate?: boolean) => void) | null;
    /** Track pending highlight to apply after render. */
    declare pendingHighlight: string | null;
    /** Axis pre-selected in the sector sweep / orbital bombardment toggle. */
    declare sectorSweepAxis: Axis;

    constructor() {
        this.panelElement = null;
        this.buffElement = null;
        this.onAbilityClick = null;
        this.pendingHighlight = null; // Track pending highlight to apply after render
        this.sectorSweepAxis = 'row'; // Default axis for sector sweep

        // Listen for opponent state updates
        abilityManager.on('opponentStateUpdated', () => {
            this.renderOpponentAbilities();
        });
    }

    // Create the ability panel element
    createAbilityPanel(container: Element, onAbilityClick: (abilityId: string, immediate?: boolean) => void): HTMLDivElement {
        this.onAbilityClick = onAbilityClick;

        // Create ability panel
        this.panelElement = document.createElement('div');
        this.panelElement.className = 'ability-panel';
        this.panelElement.innerHTML = `
            <div class="ability-panel-header">
                <h4>ABILITIES</h4>
                <div class="extra-shots-indicator hidden">
                    <span class="extra-shots-count">0</span> extra shot(s)
                </div>
            </div>
            <div class="ability-list"></div>
            <div class="ability-passives">
                <h5>PASSIVE</h5>
                <div class="passive-list"></div>
            </div>
        `;

        // Create buff indicator
        this.buffElement = document.createElement('div');
        this.buffElement.className = 'buff-indicator hidden';

        container.appendChild(this.panelElement);
        container.appendChild(this.buffElement);

        return this.panelElement;
    }

    // Render abilities in the panel
    renderAbilities(isMyTurn = false): void {
        if (!this.panelElement) return;

        const activeAbilities = abilityManager.getActiveAbilities();
        const passiveAbilities = abilityManager.getPassiveAbilities();
        const abilityList = this.panelElement.querySelector('.ability-list')!;
        const passiveList = this.panelElement.querySelector('.passive-list')!;

        // Render active abilities
        abilityList.innerHTML = '';
        for (const ability of activeAbilities) {
            const canUse = isMyTurn && abilityManager.canUseAbility(ability.id);
            const blockedReason = abilityManager.getAbilityBlockedReason(ability.id);
            const isTargeting = abilityManager.targetingAbility === ability.id;

            const upgraded = isAbilityUpgraded(ability);

            const abilityEl = document.createElement('div');
            abilityEl.className = `ability-item ${canUse ? 'available' : 'unavailable'} ${isTargeting ? 'targeting' : ''} ${upgraded ? 'upgraded' : ''}`;
            abilityEl.dataset.abilityId = ability.id;
            abilityEl.setAttribute('role', 'button');
            abilityEl.setAttribute('aria-label', `${ability.name}${canUse ? '' : ' (unavailable)'}`);
            if (!canUse) abilityEl.setAttribute('aria-disabled', 'true');

            const icon = ABILITY_ICONS[ability.icon] || ABILITY_ICONS['default'];
            const usesText = ability.maxUses !== null
                ? `${ability.usesRemaining}/${ability.maxUses}`
                : 'Unlimited';
            const cooldownText = ability.cooldownRemaining > 0
                ? `CD: ${ability.cooldownRemaining}`
                : '';

            // Build ability element using DOM methods for security
            const iconDiv = document.createElement('div');
            iconDiv.className = 'ability-icon';
            iconDiv.innerHTML = icon; // Safe - from hardcoded ABILITY_ICONS

            const infoDiv = document.createElement('div');
            infoDiv.className = 'ability-info';

            const nameDiv = document.createElement('div');
            nameDiv.className = 'ability-name';
            nameDiv.textContent = ability.name;

            const descDiv = document.createElement('div');
            descDiv.className = 'ability-desc';
            const dynamicDesc = getUpgradedDescription(ability);
            descDiv.textContent = dynamicDesc || ability.description;

            const statsDiv = document.createElement('div');
            statsDiv.className = 'ability-stats';
            const usesSpan = document.createElement('span');
            usesSpan.className = 'ability-uses';
            usesSpan.textContent = usesText;
            statsDiv.appendChild(usesSpan);

            if (cooldownText) {
                const cooldownSpan = document.createElement('span');
                cooldownSpan.className = 'ability-cooldown';
                cooldownSpan.textContent = cooldownText;
                statsDiv.appendChild(cooldownSpan);
            }

            infoDiv.appendChild(nameDiv);
            infoDiv.appendChild(descDiv);
            infoDiv.appendChild(statsDiv);

            abilityEl.appendChild(iconDiv);
            abilityEl.appendChild(infoDiv);

            // Upgrade badge
            if (upgraded) {
                const badge = document.createElement('div');
                badge.className = 'upgrade-badge';
                badge.textContent = 'UPGRADED';
                abilityEl.appendChild(badge);
            }

            if (!isMyTurn) {
                abilityEl.title = 'Wait for your turn';
            } else if (blockedReason) {
                abilityEl.title = blockedReason;
            } else {
                abilityEl.title = 'Click to use';
            }

            if (canUse) {
                abilityEl.addEventListener('click', () => this.handleAbilityClick(ability.id));
            }

            abilityList.appendChild(abilityEl);
        }

        // Render passive abilities
        passiveList.innerHTML = '';
        if (passiveAbilities.length === 0) {
            const noPassives = document.createElement('div');
            noPassives.className = 'no-passives';
            noPassives.textContent = 'None';
            passiveList.appendChild(noPassives);
        } else {
            for (const ability of passiveAbilities) {
                const passiveUpgraded = isAbilityUpgraded(ability);

                const passiveEl = document.createElement('div');
                passiveEl.className = `passive-item ${passiveUpgraded ? 'upgraded' : ''}`;
                passiveEl.dataset.abilityId = ability.id;

                // Add unavailable class if on cooldown or no uses
                const onCooldown = ability.cooldownRemaining > 0;
                const noUses = ability.usesRemaining !== null && ability.usesRemaining <= 0;
                if (onCooldown || noUses) {
                    passiveEl.classList.add('passive-unavailable');
                }

                const iconDiv = document.createElement('div');
                iconDiv.className = 'passive-icon';
                iconDiv.innerHTML = ABILITY_ICONS[ability.icon] || ABILITY_ICONS['default']; // Safe - hardcoded

                const infoDiv = document.createElement('div');
                infoDiv.className = 'passive-info';

                const nameSpan = document.createElement('span');
                nameSpan.className = 'passive-name';
                nameSpan.textContent = ability.name;
                if (passiveUpgraded) {
                    const passiveBadge = document.createElement('span');
                    passiveBadge.className = 'passive-upgrade-badge';
                    passiveBadge.textContent = ' UPGRADED';
                    nameSpan.appendChild(passiveBadge);
                }

                const triggerSpan = document.createElement('span');
                triggerSpan.className = 'passive-trigger';
                triggerSpan.textContent = this.formatTrigger(ability.trigger);

                const passiveDynamicDesc = getUpgradedDescription(ability);
                const descSpan = document.createElement('span');
                descSpan.className = 'passive-desc';
                descSpan.textContent = passiveDynamicDesc || ability.description;

                infoDiv.appendChild(nameSpan);
                infoDiv.appendChild(triggerSpan);
                infoDiv.appendChild(descSpan);

                // Add stats for cooldowns and uses if applicable
                const hasUses = ability.maxUses !== null && ability.maxUses !== undefined;
                const hasCooldown = ability.cooldownTurns > 0;

                if (hasUses || hasCooldown) {
                    const statsDiv = document.createElement('div');
                    statsDiv.className = 'passive-stats';

                    if (hasUses) {
                        const usesSpan = document.createElement('span');
                        usesSpan.className = 'passive-uses';
                        usesSpan.textContent = `${ability.usesRemaining}/${ability.maxUses} uses`;
                        if (ability.usesRemaining! <= 0) {
                            usesSpan.classList.add('depleted');
                        }
                        statsDiv.appendChild(usesSpan);
                    }

                    if (hasCooldown) {
                        const cooldownSpan = document.createElement('span');
                        cooldownSpan.className = 'passive-cooldown';
                        if (ability.cooldownRemaining > 0) {
                            cooldownSpan.textContent = `${ability.cooldownRemaining} turn cooldown`;
                            cooldownSpan.classList.add('on-cooldown');
                        } else {
                            cooldownSpan.textContent = `${ability.cooldownTurns}T CD`;
                        }
                        statsDiv.appendChild(cooldownSpan);
                    }

                    infoDiv.appendChild(statsDiv);
                }

                passiveEl.appendChild(iconDiv);
                passiveEl.appendChild(infoDiv);
                passiveList.appendChild(passiveEl);
            }
        }

        // Update extra shots indicator
        this.updateExtraShotsIndicator();

        // Apply any pending highlight after render
        if (this.pendingHighlight) {
            const abilityId = this.pendingHighlight;
            this.pendingHighlight = null;
            // Delay slightly to ensure DOM is updated
            setTimeout(() => this.highlightPassive(abilityId), 50);
        }
    }

    // Render opponent's ability state to #enemy-abilities under opponent's board
    renderOpponentAbilities(): void {
        const container = document.getElementById('enemy-abilities');
        if (!container) return;

        const opponentState = abilityManager.getOpponentState();

        if (!opponentState || !opponentState.abilities) {
            container.classList.add('hidden');
            return;
        }

        container.classList.remove('hidden');
        container.textContent = ''; // Clear existing content
        container.className = 'enemy-abilities opponent-abilities-tracker';

        // Create header
        const header = document.createElement('div');
        header.className = 'opp-tracker-header';

        const title = document.createElement('h5');
        title.textContent = 'OPPONENT ABILITIES';
        header.appendChild(title);

        // Extra shots indicator in header
        if (opponentState.extraShots > 0) {
            const extraShots = document.createElement('span');
            extraShots.className = 'opp-extra-shots';
            extraShots.textContent = `+${opponentState.extraShots} Extra Shot${opponentState.extraShots > 1 ? 's' : ''}`;
            header.appendChild(extraShots);
        }

        container.appendChild(header);

        // Create ability list
        const list = document.createElement('div');
        list.className = 'opp-tracker-list';

        // Render each ability
        for (const [id, state] of Object.entries(opponentState.abilities)) {
            const item = document.createElement('div');
            item.className = 'opp-tracker-ability';

            // Check states
            const isDepleted = state.usesRemaining !== null && state.usesRemaining <= 0;
            const onCooldown = state.cooldownRemaining > 0;
            if (isDepleted) item.classList.add('depleted');
            if (onCooldown) item.classList.add('on-cooldown');

            // Icon
            const iconDiv = document.createElement('div');
            iconDiv.className = 'opp-ability-icon';
            const iconKey = state.icon || 'default';
            renderIcon(iconDiv, iconKey);
            item.appendChild(iconDiv);

            // Info section
            const infoDiv = document.createElement('div');
            infoDiv.className = 'opp-ability-info';

            const nameDiv = document.createElement('div');
            nameDiv.className = 'opp-ability-name';
            nameDiv.textContent = state.name || id;
            infoDiv.appendChild(nameDiv);

            const statusDiv = document.createElement('div');
            statusDiv.className = 'opp-ability-status';

            // Uses text
            if (state.type === 'passive') {
                const passiveSpan = document.createElement('span');
                passiveSpan.textContent = 'Passive';
                statusDiv.appendChild(passiveSpan);
            } else {
                const usesSpan = document.createElement('span');
                usesSpan.textContent = state.usesRemaining !== null
                    ? `${state.usesRemaining}/${state.maxUses} uses`
                    : '∞ uses';
                statusDiv.appendChild(usesSpan);
            }

            // Cooldown text
            if (onCooldown) {
                const cdSpan = document.createElement('span');
                cdSpan.className = 'opp-cooldown';
                cdSpan.textContent = `CD: ${state.cooldownRemaining}`;
                statusDiv.appendChild(cdSpan);
            }

            infoDiv.appendChild(statusDiv);
            item.appendChild(infoDiv);

            // Type badge
            const badge = document.createElement('span');
            badge.className = `opp-type-badge ${state.type || 'active'}`;
            badge.textContent = state.type === 'passive' ? 'Passive' : 'Active';
            item.appendChild(badge);

            list.appendChild(item);
        }

        container.appendChild(list);

        // Show active buffs if any
        if (opponentState.buffs && Object.keys(opponentState.buffs).length > 0) {
            const buffsDiv = document.createElement('div');
            buffsDiv.className = 'opp-tracker-buffs';

            for (const [buffId, buff] of Object.entries(opponentState.buffs)) {
                const buffItem = document.createElement('div');
                buffItem.className = 'opp-buff-item';

                const buffName = buffId.replace(/_/g, ' ');
                if (buff.shotsRemaining) {
                    buffItem.textContent = `${buffName} (${buff.shotsRemaining} shots)`;
                } else {
                    buffItem.textContent = buffName;
                }
                buffsDiv.appendChild(buffItem);
            }

            container.appendChild(buffsDiv);
        }
    }

    // Format trigger text for display
    formatTrigger(trigger: string | undefined): string {
        const triggerMap: Record<string, string> = {
            'on_turn_start': 'Turn Start',
            'on_incoming_hit': 'When Hit',
            'on_ship_lost': 'Ship Lost',
            'on_hit_enemy': 'On Hit',
            'on_enemy_miss': 'Enemy Miss',
            'on_ship_destroyed': 'Ship Destroyed'
        };
        return triggerMap[trigger!] || trigger!;
    }

    // Abilities that don't require targeting (activate immediately)
    static NO_TARGET_ABILITIES = ['multi_shot', 'scrap_salvo', 'kamikaze_drone'];

    // Handle ability click
    handleAbilityClick(abilityId: string): void {
        if (abilityManager.targetingAbility === abilityId) {
            // Cancel if clicking same ability
            if (typeof window.clearRepairSelection === 'function') {
                window.clearRepairSelection();
            }
            abilityManager.cancelTargeting();
            this.hideTargetingMode();
            this.renderAbilities(true);
        } else if (AbilityRenderer.NO_TARGET_ABILITIES.includes(abilityId)) {
            // Abilities that don't need targeting - activate immediately
            if (abilityManager.canUseAbility(abilityId)) {
                if (this.onAbilityClick) {
                    this.onAbilityClick(abilityId, true); // true = immediate activation
                }
            }
        } else {
            // Select ability for targeting
            if (abilityManager.selectAbility(abilityId)) {
                this.renderAbilities(true);
                if (this.onAbilityClick) {
                    this.onAbilityClick(abilityId, false);
                }
            }
        }
    }

    // Update the extra shots indicator
    updateExtraShotsIndicator(): void {
        if (!this.panelElement) return;

        const indicator = this.panelElement.querySelector('.extra-shots-indicator')!;
        const count = this.panelElement.querySelector('.extra-shots-count')!;

        if (abilityManager.hasExtraShots()) {
            indicator.classList.remove('hidden');
            count.textContent = String(abilityManager.extraShots);
        } else {
            indicator.classList.add('hidden');
        }
    }

    // Show buff indicator
    showBuff(buffName: string, buffData?: BuffStateWire | null): void {
        if (!this.buffElement) return;

        let text;
        if (buffName === 'splash_damage') {
            text = (buffData?.splashRadius ?? 0) > 1
                ? `SPLASH DAMAGE ACTIVE (radius ${buffData!.splashRadius}) - Next shot hits expanded area!`
                : 'SPLASH DAMAGE ACTIVE - Next shot hits adjacent cells!';
        } else {
            text = `BUFF: ${buffName}`;
        }

        this.buffElement.textContent = text;
        this.buffElement.classList.remove('hidden');
        this.buffElement.classList.add('active');
    }

    // Hide buff indicator
    hideBuff(_buffName?: string): void {
        if (!this.buffElement) return;
        this.buffElement.classList.add('hidden');
        this.buffElement.classList.remove('active');
    }

    // Update buffs display
    updateBuffs(): void {
        if (!this.buffElement) return;

        const state = abilityManager.getState();
        if (state.buffs && Object.keys(state.buffs).length > 0) {
            const buffName = Object.keys(state.buffs)[0];
            this.showBuff(buffName, state.buffs[buffName]);
        } else {
            this.hideBuff();
        }
    }

    // Show ability result feedback (with HTML escaping)
    showAbilityResult(result: AbilityResultNotice): void {
        // Create floating notification
        const notification = document.createElement('div');
        notification.className = `ability-notification ${result.success ? 'success' : 'error'}`;

        const titleDiv = document.createElement('div');
        titleDiv.className = 'ability-notification-title';
        titleDiv.textContent = result.abilityName || 'Ability';

        const messageDiv = document.createElement('div');
        messageDiv.className = 'ability-notification-message';
        messageDiv.textContent = result.message;

        notification.appendChild(titleDiv);
        notification.appendChild(messageDiv);
        document.body.appendChild(notification);

        // Animate and remove (guard against stale DOM)
        setTimeout(() => {
            if (notification.isConnected) {
                notification.classList.add('fade-out');
                setTimeout(() => notification.remove(), 500);
            }
        }, 2500);
    }

    // Show passive trigger notification (with HTML escaping)
    showPassiveTriggered(result: PassiveNotice): void {
        const notification = document.createElement('div');
        notification.className = 'passive-notification';

        const titleDiv = document.createElement('div');
        titleDiv.className = 'passive-notification-title';
        titleDiv.textContent = result.abilityName!;

        const messageDiv = document.createElement('div');
        messageDiv.className = 'passive-notification-message';
        messageDiv.textContent = result.message!;

        notification.appendChild(titleDiv);
        notification.appendChild(messageDiv);
        document.body.appendChild(notification);

        // Set pending highlight - will be applied after next render or immediately
        if (result.abilityId) {
            this.pendingHighlight = result.abilityId;
            // Also try to highlight immediately in case no re-render happens
            setTimeout(() => this.highlightPassive(result.abilityId!), 100);
        }

        setTimeout(() => {
            if (notification.isConnected) {
                notification.classList.add('fade-out');
                setTimeout(() => notification.remove(), 500);
            }
        }, 2500);
    }

    // Highlight a passive ability in the panel when it activates
    highlightPassive(abilityId: string): void {
        // Query from document to ensure we find the element
        const passiveItems = document.querySelectorAll<HTMLElement>('.passive-item');
        let passiveItem: HTMLElement | null = null;
        for (const item of passiveItems) {
            if (item.dataset.abilityId === abilityId) {
                passiveItem = item;
                break;
            }
        }

        if (!passiveItem) return;

        // Remove any existing highlight first
        passiveItem.classList.remove('passive-activated');
        forceReflow(passiveItem);
        passiveItem.classList.add('passive-activated');

        setTimeout(() => {
            passiveItem.classList.remove('passive-activated');
        }, 2500);
    }

    // Show revealed cells from recon scan
    showRevealedCells(revealed: RevealedCell[] | null | undefined, boardElement: HTMLElement): void {
        if (!revealed || revealed.length === 0) return;

        // Compute bounding box for scan grid overlay
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const cell of revealed) {
            minX = Math.min(minX, cell.x);
            minY = Math.min(minY, cell.y);
            maxX = Math.max(maxX, cell.x);
            maxY = Math.max(maxY, cell.y);
        }

        // Show scan grid overlay indicating the scan area size
        const gridW = maxX - minX + 1;
        const gridH = maxY - minY + 1;
        const topLeftCell = boardElement.querySelector(`[data-x="${minX}"][data-y="${minY}"]`);
        const bottomRightCell = boardElement.querySelector(`[data-x="${maxX}"][data-y="${maxY}"]`);

        if (topLeftCell && bottomRightCell) {
            const overlay = document.createElement('div');
            overlay.className = 'scan-grid-overlay';

            const boardRect = boardElement.getBoundingClientRect();
            const tlRect = topLeftCell.getBoundingClientRect();
            const brRect = bottomRightCell.getBoundingClientRect();

            overlay.style.left = `${tlRect.left - boardRect.left}px`;
            overlay.style.top = `${tlRect.top - boardRect.top}px`;
            overlay.style.width = `${brRect.right - tlRect.left}px`;
            overlay.style.height = `${brRect.bottom - tlRect.top}px`;

            // Show grid size label (e.g. "4×4" or "5×5")
            const label = document.createElement('span');
            label.className = 'scan-grid-label';
            label.textContent = `${gridW}×${gridH} SCAN`;
            overlay.appendChild(label);

            boardElement.appendChild(overlay);

            setTimeout(() => {
                overlay.classList.add('fade-out');
                setTimeout(() => overlay.remove(), 500);
            }, 2800);
        }

        // Individual cell reveals
        for (const cell of revealed) {
            const cellEl = boardElement.querySelector(`[data-x="${cell.x}"][data-y="${cell.y}"]`);
            if (cellEl) {
                cellEl.classList.add('revealed');
                if (cell.hasShip) {
                    cellEl.classList.add('revealed-ship');
                } else {
                    cellEl.classList.add('revealed-empty');
                }

                // Remove reveal effect after animation
                setTimeout(() => {
                    cellEl.classList.remove('revealed', 'revealed-ship', 'revealed-empty');
                }, 3000);
            }
        }
    }

    // Show shield deflection effect
    showShieldDeflection(x: number, y: number, boardElement: HTMLElement): void {
        const cellEl = boardElement.querySelector<HTMLElement>(`[data-x="${x}"][data-y="${y}"]`);
        if (cellEl) {
            cellEl.classList.remove('shield-deflect');
            forceReflow(cellEl);
            cellEl.classList.add('shield-deflect');

            setTimeout(() => {
                cellEl.classList.remove('shield-deflect');
            }, 800);
        }
    }

    // Show splash damage activation effect (board-wide pulse)
    showSplashDamageActivation(boardElement: HTMLElement): void {
        const boardSection = boardElement.closest<HTMLElement>('.board-section');
        if (boardSection) {
            boardSection.classList.remove('splash-damage-activated');
            forceReflow(boardSection);
            boardSection.classList.add('splash-damage-activated');

            setTimeout(() => {
                boardSection.classList.remove('splash-damage-activated');
            }, 600);
        }
    }

    // Show double/multi shot activation effect
    showDoubleShotActivation(boardElement: HTMLElement, extraShots = 1): void {
        const boardSection = boardElement.closest<HTMLElement>('.board-section');
        if (boardSection) {
            boardSection.classList.remove('double-shot-active');
            forceReflow(boardSection);
            boardSection.classList.add('double-shot-active');

            // Show shot count overlay for upgraded multi-shot
            if (extraShots > 1) {
                const label = document.createElement('div');
                label.className = 'multi-shot-label';
                label.textContent = `×${extraShots + 1} SHOTS`;
                boardSection.appendChild(label);
                setTimeout(() => {
                    label.classList.add('fade-out');
                    setTimeout(() => label.remove(), 500);
                }, 1500);
            }

            setTimeout(() => {
                boardSection.classList.remove('double-shot-active');
            }, 500);
        }
    }

    // Show sector sweep result - highlight row or column
    showSectorSweep(axis: Axis | undefined, index: number | undefined, hasShip: boolean | undefined, boardElement: HTMLElement): void {
        const cells = axis === 'row'
            ? boardElement.querySelectorAll(`[data-y="${index}"]`)
            : boardElement.querySelectorAll(`[data-x="${index}"]`);

        for (const cell of cells) {
            cell.classList.add('sector-sweep');
            if (hasShip) {
                cell.classList.add('sector-detected');
            }
        }

        setTimeout(() => {
            for (const cell of cells) {
                cell.classList.remove('sector-sweep', 'sector-detected');
            }
        }, 2000);
    }

    // Show relentless assault trigger - chain effect
    showRelentlessAssault(boardElement: HTMLElement): void {
        const boardSection = boardElement.closest<HTMLElement>('.board-section');
        if (boardSection) {
            boardSection.classList.add('relentless-active');
            setTimeout(() => {
                boardSection.classList.remove('relentless-active');
            }, 500);
        }
    }

    // Show repair effect - healing glow on cell
    showRepairEffect(x: number, y: number, boardElement: HTMLElement): void {
        const cellEl = boardElement.querySelector(`[data-x="${x}"][data-y="${y}"]`);
        if (cellEl) {
            cellEl.classList.add('repair-effect');
            setTimeout(() => {
                cellEl.classList.remove('repair-effect');
            }, 800);
        }
    }

    // Show decoy placement - shimmer effect
    showDecoyPlacement(cells: Coord[], board: HTMLElement): void {
        for (const { x, y } of cells) {
            const cellEl = board.querySelector(`[data-x="${x}"][data-y="${y}"]`);
            if (cellEl) {
                cellEl.classList.add('decoy-placed');
            }
        }
    }

    // Show decoy reveal - hologram break effect
    showDecoyReveal(cells: Coord[], board: HTMLElement): void {
        for (const { x, y } of cells) {
            const cellEl = board.querySelector(`[data-x="${x}"][data-y="${y}"]`);
            if (cellEl) {
                cellEl.classList.remove('decoy-placed');
                cellEl.classList.add('decoy-revealed');
                setTimeout(() => {
                    cellEl.classList.remove('decoy-revealed');
                }, 1500);
            }
        }
    }

    // Show ship scanner reveal animation - reveals ship location cells with staggered animation
    showShipScannerReveal(revealed: RevealedCell[] | null | undefined, boardElement: HTMLElement, hitX?: number, hitY?: number): void {
        if (!revealed || revealed.length === 0) return;

        // Sort cells by distance from hit location for sequential reveal.
        // (Without hit coords the distances are NaN and the comparator leaves
        // the order untouched — same as the historical untyped behavior.)
        const sortedCells = [...revealed].sort((a, b) => {
            const distA = Math.abs(a.x - (hitX ?? NaN)) + Math.abs(a.y - (hitY ?? NaN));
            const distB = Math.abs(b.x - (hitX ?? NaN)) + Math.abs(b.y - (hitY ?? NaN));
            return distA - distB;
        });

        // Stagger the reveal animation for each cell
        sortedCells.forEach((cell, index) => {
            const cellEl = boardElement.querySelector(`[data-x="${cell.x}"][data-y="${cell.y}"]`);
            if (cellEl) {
                // Stagger delay based on position in sequence
                const delay = index * 100; // 100ms between each cell

                setTimeout(() => {
                    // Add reveal animation class
                    cellEl.classList.add('scanner-reveal');

                    // After initial animation, switch to persistent state
                    setTimeout(() => {
                        cellEl.classList.remove('scanner-reveal');
                        cellEl.classList.add('scanner-reveal-persist');

                        // Remove persistent state after total display time
                        setTimeout(() => {
                            cellEl.classList.remove('scanner-reveal-persist');
                        }, 2500);
                    }, 600); // Match animation duration
                }, delay);
            }
        });
    }

    // Show opponent precision strike indicator
    showOpponentPrecision(x: number, y: number, boardElement: HTMLElement): void {
        const cellEl = boardElement.querySelector(`[data-x="${x}"][data-y="${y}"]`);
        if (cellEl) {
            const indicator = document.createElement('div');
            indicator.className = 'precision-indicator opponent';
            indicator.textContent = 'SCANNED';
            cellEl.appendChild(indicator);

            setTimeout(() => {
                indicator.classList.add('fade-out');
                setTimeout(() => indicator.remove(), 500);
            }, 2000);
        }
    }

    // Show targeting mode indicator (with HTML escaping)
    showTargetingMode(ability: ClientAbilityState, repairMaxCells = 1): void {
        // Remove any existing targeting indicator to prevent duplicate IDs
        this.hideTargetingMode();

        const indicator = document.createElement('div');
        indicator.id = 'targeting-mode-indicator';
        indicator.className = 'targeting-mode-indicator';

        const textDiv = document.createElement('div');
        textDiv.className = 'targeting-text';

        // Multi-cell repair targeting
        if (ability.id === 'repair' && repairMaxCells > 1) {
            textDiv.textContent = `SELECT UP TO ${repairMaxCells} DAMAGED CELLS TO REPAIR`;
            indicator.appendChild(textDiv);

            const counterDiv = document.createElement('div');
            counterDiv.id = 'repair-target-counter';
            counterDiv.className = 'targeting-hint';
            counterDiv.textContent = `0 / ${repairMaxCells} cells selected`;
            indicator.appendChild(counterDiv);

            const confirmBtn = document.createElement('button');
            confirmBtn.id = 'repair-confirm-btn';
            confirmBtn.className = 'targeting-confirm';
            confirmBtn.textContent = 'CONFIRM REPAIR';
            confirmBtn.disabled = true;
            confirmBtn.addEventListener('click', () => {
                // confirmRepairTargets is defined in main.ts
                if (typeof window.confirmRepairTargets === 'function') {
                    window.confirmRepairTargets();
                }
            });
            indicator.appendChild(confirmBtn);

        // Special handling for sector_sweep and orbital_bombardment - add axis toggle
        } else if (ability.id === 'sector_sweep' || ability.id === 'orbital_bombardment') {
            textDiv.textContent = 'SELECT A ';

            const toggleWrapper = document.createElement('div');
            toggleWrapper.className = 'sector-sweep-toggle';

            const rowBtn = document.createElement('button');
            rowBtn.className = `sector-toggle-btn ${this.sectorSweepAxis === 'row' ? 'active' : ''}`;
            rowBtn.textContent = 'ROW';
            rowBtn.addEventListener('click', () => {
                this.sectorSweepAxis = 'row';
                rowBtn.classList.add('active');
                colBtn.classList.remove('active');
            });

            const colBtn = document.createElement('button');
            colBtn.className = `sector-toggle-btn ${this.sectorSweepAxis === 'column' ? 'active' : ''}`;
            colBtn.textContent = 'COLUMN';
            colBtn.addEventListener('click', () => {
                this.sectorSweepAxis = 'column';
                colBtn.classList.add('active');
                rowBtn.classList.remove('active');
            });

            toggleWrapper.appendChild(rowBtn);
            toggleWrapper.appendChild(colBtn);
            indicator.appendChild(textDiv);
            indicator.appendChild(toggleWrapper);
        } else if (ability.id === 'decoy' && ((ability.params?.decoySize as number | undefined) || 1) > 1) {
            // Multi-cell decoy - show rotation hint
            textDiv.textContent = `PLACE ${ability.params.decoySize}-CELL DECOY`;
            indicator.appendChild(textDiv);

            const hintDiv = document.createElement('div');
            hintDiv.className = 'targeting-hint';
            hintDiv.textContent = 'Right-click or press R to rotate';
            indicator.appendChild(hintDiv);
        } else {
            textDiv.textContent = `SELECT TARGET FOR: ${ability.name.toUpperCase()}`;
            indicator.appendChild(textDiv);
        }

        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'targeting-cancel';
        cancelBtn.textContent = 'CANCEL (ESC)';
        cancelBtn.addEventListener('click', () => {
            // Clear multi-cell repair selection if active
            if (typeof window.clearRepairSelection === 'function') {
                window.clearRepairSelection();
            }
            abilityManager.cancelTargeting();
            this.hideTargetingMode();
            this.renderAbilities(true);
        });

        indicator.appendChild(cancelBtn);
        document.body.appendChild(indicator);
    }

    // Hide targeting mode indicator
    hideTargetingMode(): void {
        const indicator = document.getElementById('targeting-mode-indicator');
        if (indicator) {
            indicator.remove();
        }
    }

    // === X1 EXPANSION ABILITY ANIMATIONS ===

    // Scrap Salvo - staggered orange shrapnel explosions on target cells
    showScrapSalvo(targets: Coord[] | null | undefined, boardElement: HTMLElement): void {
        if (!targets || !boardElement) return;

        targets.forEach((target, i) => {
            const cellEl = boardElement.querySelector<HTMLElement>(`[data-x="${target.x}"][data-y="${target.y}"]`);
            if (!cellEl) return;

            setTimeout(() => {
                cellEl.classList.add('scrap-impact');

                // Spawn shrapnel particles
                for (let p = 0; p < 6; p++) {
                    const particle = document.createElement('div');
                    particle.className = 'scrap-particle';
                    const angle = (p / 6) * Math.PI * 2 + (Math.random() - 0.5);
                    const dist = 15 + Math.random() * 20;
                    particle.style.setProperty('--tx', `${Math.cos(angle) * dist}px`);
                    particle.style.setProperty('--ty', `${Math.sin(angle) * dist}px`);
                    particle.style.setProperty('--rot', `${Math.random() * 360}deg`);
                    particle.style.left = '50%';
                    particle.style.top = '50%';
                    cellEl.appendChild(particle);
                    setTimeout(() => particle.remove(), 600);
                }

                setTimeout(() => cellEl.classList.remove('scrap-impact'), 700);
            }, i * 100);
        });
    }

    // Kamikaze Drone - implosion then explosion on target cell
    showKamikazeDrone(targetHit: Coord | null | undefined, boardElement: HTMLElement): void {
        if (!targetHit || !boardElement) return;

        const cellEl = boardElement.querySelector(`[data-x="${targetHit.x}"][data-y="${targetHit.y}"]`);
        if (!cellEl) return;

        cellEl.classList.add('kamikaze-dive');
        setTimeout(() => {
            cellEl.classList.remove('kamikaze-dive');
            cellEl.classList.add('kamikaze-explode');
            setTimeout(() => cellEl.classList.remove('kamikaze-explode'), 800);
        }, 400);
    }

    // Kamikaze Drone Reveal - green-tinted reveal on exposed cells (penalty)
    showKamikazeDroneReveal(revealedCells: RevealedCell[] | null | undefined, boardElement: HTMLElement): void {
        if (!revealedCells || !boardElement) return;

        for (const cell of revealedCells) {
            const cellEl = boardElement.querySelector(`[data-x="${cell.x}"][data-y="${cell.y}"]`);
            if (cellEl) {
                cellEl.classList.add('kamikaze-reveal');
                if (cell.hasShip) {
                    cellEl.classList.add('revealed-ship');
                } else {
                    cellEl.classList.add('revealed-empty');
                }
                setTimeout(() => {
                    cellEl.classList.remove('kamikaze-reveal');
                    cellEl.classList.remove('revealed-ship', 'revealed-empty');
                }, 3000);
            }
        }
    }

    // Guerrilla Tactics - digital glitch on board section
    showGuerrillaTactics(boardElement: HTMLElement): void {
        if (!boardElement) return;

        const boardSection = boardElement.closest<HTMLElement>('.board-section');
        if (boardSection) {
            boardSection.classList.remove('guerrilla-active');
            forceReflow(boardSection);
            boardSection.classList.add('guerrilla-active');
            setTimeout(() => boardSection.classList.remove('guerrilla-active'), 600);
        }
    }

    // Orbital Bombardment - golden beam sweep on row/col + strike on hit cells
    // hitTargets can be a single {x,y} object or an array of {x,y} objects
    showOrbitalBombardment(
        axis: Axis | undefined,
        index: number | undefined,
        hitTargets: Coord | Coord[] | null | undefined,
        boardElement: HTMLElement
    ): void {
        if (!boardElement || index === undefined) return;

        // Highlight the swept row/column
        const cells = axis === 'row'
            ? boardElement.querySelectorAll(`[data-y="${index}"]`)
            : boardElement.querySelectorAll(`[data-x="${index}"]`);

        for (const cell of cells) {
            cell.classList.add('orbital-sweep');
            setTimeout(() => cell.classList.remove('orbital-sweep'), 500);
        }

        // Normalize to array
        const targets = hitTargets
            ? (Array.isArray(hitTargets) ? hitTargets : [hitTargets])
            : [];

        // Show beam + impact on each hit cell, staggered
        targets.forEach((target, i) => {
            const hitCell = boardElement.querySelector(`[data-x="${target.x}"][data-y="${target.y}"]`);
            if (hitCell) {
                setTimeout(() => {
                    hitCell.classList.add('orbital-target');
                    setTimeout(() => {
                        hitCell.classList.remove('orbital-target');
                        hitCell.classList.add('orbital-impact');
                        setTimeout(() => hitCell.classList.remove('orbital-impact'), 800);
                    }, 300);
                }, 200 + i * 250);
            }
        });

        // Show hit count label for multi-hit
        if (targets.length > 1) {
            const boardSection = boardElement.closest<HTMLElement>('.board-section');
            if (boardSection) {
                const label = document.createElement('div');
                label.className = 'orbital-hit-count';
                label.textContent = `${targets.length} TARGETS LOCKED`;
                boardSection.appendChild(label);
                setTimeout(() => {
                    label.classList.add('fade-out');
                    setTimeout(() => label.remove(), 500);
                }, 2000);
            }
        }
    }

    // Inquisitor's Mandate - gold iris lock-on on revealed cells (initiator view)
    showInquisitorMandate(revealed: RevealedCell[] | null | undefined, boardElement: HTMLElement): void {
        if (!revealed || !boardElement) return;

        revealed.forEach((cell, i) => {
            const cellEl = boardElement.querySelector(`[data-x="${cell.x}"][data-y="${cell.y}"]`);
            if (cellEl) {
                setTimeout(() => {
                    cellEl.classList.add('inquisitor-lock');
                    setTimeout(() => cellEl.classList.remove('inquisitor-lock'), 2500);
                }, i * 100);
            }
        });
    }

    // Inquisitor's Mandate - scanned effect (opponent view)
    showInquisitorScanned(revealed: RevealedCell[] | null | undefined, boardElement: HTMLElement): void {
        if (!revealed || !boardElement) return;

        revealed.forEach((cell, i) => {
            const cellEl = boardElement.querySelector(`[data-x="${cell.x}"][data-y="${cell.y}"]`);
            if (cellEl) {
                setTimeout(() => {
                    cellEl.classList.add('inquisitor-scanned');
                    setTimeout(() => cellEl.classList.remove('inquisitor-scanned'), 2500);
                }, i * 100);
            }
        });
    }

    // Iron Might - gold surge on player's board section + ability cards
    showIronMight(): void {
        // Surge on player board section
        const playerBoard = document.getElementById('your-board');
        if (playerBoard) {
            const boardSection = playerBoard.closest<HTMLElement>('.board-section');
            if (boardSection) {
                boardSection.classList.remove('iron-surge');
                forceReflow(boardSection);
                boardSection.classList.add('iron-surge');
                setTimeout(() => boardSection.classList.remove('iron-surge'), 600);
            }
        }

        // Stagger surge on ability cards
        const abilityItems = document.querySelectorAll<HTMLElement>('.ability-item');
        abilityItems.forEach((item, i) => {
            setTimeout(() => {
                item.classList.remove('surge-active');
                forceReflow(item);
                item.classList.add('surge-active');
                setTimeout(() => item.classList.remove('surge-active'), 800);
            }, i * 100);
        });
    }

    // === SEVERIAN PRINCIPATE ABILITY ANIMATIONS ===

    // Probe Network - show pulsing detection zone on enemy board
    showProbeDeployed(probePos: RowCol | null | undefined, detectionRadius: number, boardElement: HTMLElement): void {
        if (!boardElement || !probePos) return;

        const halfSize = Math.floor(detectionRadius / 2);

        for (let dy = -halfSize; dy <= halfSize; dy++) {
            for (let dx = -halfSize; dx <= halfSize; dx++) {
                const nx = probePos.col + dx;
                const ny = probePos.row + dy;
                const cellEl = boardElement.querySelector(`[data-x="${nx}"][data-y="${ny}"]`);
                if (cellEl) {
                    cellEl.classList.add('probe-zone');
                    if (dx === 0 && dy === 0) {
                        cellEl.classList.add('probe-center');
                    }
                }
            }
        }

        // Fade zone after initial display, but keep persistent marker on center
        setTimeout(() => {
            boardElement.querySelectorAll('.probe-zone').forEach(el => {
                el.classList.add('probe-zone-fade');
                setTimeout(() => {
                    el.classList.remove('probe-zone', 'probe-zone-fade', 'probe-center');
                }, 500);
            });
            // Add persistent active marker on probe center cell
            const centerCell = boardElement.querySelector(`[data-x="${probePos.col}"][data-y="${probePos.row}"]`);
            if (centerCell) {
                centerCell.classList.add('probe-active');
            }
        }, 2500);
    }

    // Probe pulse - flash detection area with contact/clear result
    showProbePulse(probePos: RowCol | null | undefined, detectionRadius: number, hasContact: boolean | undefined, boardElement: HTMLElement): void {
        if (!boardElement || !probePos) return;

        const halfSize = Math.floor(detectionRadius / 2);
        const contactClass = hasContact ? 'probe-contact' : 'probe-clear';

        for (let dy = -halfSize; dy <= halfSize; dy++) {
            for (let dx = -halfSize; dx <= halfSize; dx++) {
                const nx = probePos.col + dx;
                const ny = probePos.row + dy;
                const cellEl = boardElement.querySelector(`[data-x="${nx}"][data-y="${ny}"]`);
                if (cellEl) {
                    cellEl.classList.add('probe-pulse', contactClass);
                    setTimeout(() => {
                        cellEl.classList.remove('probe-pulse', contactClass);
                    }, 2000);
                }
            }
        }

        // Show floating label
        const centerCell = boardElement.querySelector(`[data-x="${probePos.col}"][data-y="${probePos.row}"]`);
        if (centerCell) {
            const label = document.createElement('div');
            label.className = `probe-result-label ${hasContact ? 'contact' : 'clear'}`;
            label.textContent = hasContact ? 'CONTACT' : 'CLEAR';
            centerCell.appendChild(label);
            setTimeout(() => {
                label.classList.add('fade-out');
                setTimeout(() => label.remove(), 500);
            }, 1800);
        }
    }

    // Hunter-Killer Mine deployment - show mine indicator
    showMineDeployed(minePos: RowCol | null | undefined, seekRadius: number | undefined, enhanced: boolean | undefined, boardElement: HTMLElement): void {
        if (!boardElement || !minePos) return;

        const cellEl = boardElement.querySelector(`[data-x="${minePos.col}"][data-y="${minePos.row}"]`);
        if (cellEl) {
            cellEl.classList.add('mine-deployed');
            if (enhanced) {
                cellEl.classList.add('mine-enhanced');
            }

            const label = document.createElement('div');
            label.className = 'mine-deploy-label';
            label.textContent = enhanced ? 'ENHANCED MINE' : 'MINE ARMED';
            cellEl.appendChild(label);
            setTimeout(() => {
                label.classList.add('fade-out');
                setTimeout(() => label.remove(), 500);
            }, 2000);
        }
    }

    // Mine detonation - explosion effect on hit targets
    showMineDetonation(minePos: RowCol | null | undefined, hitTargets: Coord[] | null | undefined, boardElement: HTMLElement): void {
        if (!boardElement) return;

        // Show mine origin flash
        if (minePos) {
            const mineCell = boardElement.querySelector(`[data-x="${minePos.col}"][data-y="${minePos.row}"]`);
            if (mineCell) {
                mineCell.classList.remove('mine-deployed', 'mine-enhanced');
                mineCell.classList.add('mine-detonate-origin');
                setTimeout(() => mineCell.classList.remove('mine-detonate-origin'), 800);
            }
        }

        // Show impacts on hit targets
        if (hitTargets) {
            hitTargets.forEach((target, i) => {
                const cellEl = boardElement.querySelector(`[data-x="${target.x}"][data-y="${target.y}"]`);
                if (cellEl) {
                    setTimeout(() => {
                        cellEl.classList.add('mine-impact');
                        setTimeout(() => cellEl.classList.remove('mine-impact'), 800);
                    }, 200 + i * 150);
                }
            });
        }
    }

    // Remove persistent probe marker when probe expires
    removeProbeMarker(probePos: RowCol | null | undefined, boardElement: HTMLElement): void {
        if (!boardElement || !probePos) return;
        const cellEl = boardElement.querySelector(`[data-x="${probePos.col}"][data-y="${probePos.row}"]`);
        if (cellEl) {
            cellEl.classList.remove('probe-active');
        }
    }

    // Remove persistent mine marker when mine expires (without detonation)
    removeMineMarker(minePos: RowCol | null | undefined, boardElement: HTMLElement): void {
        if (!boardElement || !minePos) return;
        const cellEl = boardElement.querySelector(`[data-x="${minePos.col}"][data-y="${minePos.row}"]`);
        if (cellEl) {
            cellEl.classList.remove('mine-deployed', 'mine-enhanced');
        }
    }

    // Damage Control heal effect - blue-green repair glow
    showDamageControlHeal(healedCells: Coord[] | null | undefined, boardElement: HTMLElement): void {
        if (!boardElement || !healedCells) return;

        healedCells.forEach((cell, i) => {
            const cellEl = boardElement.querySelector(`[data-x="${cell.x}"][data-y="${cell.y}"]`);
            if (cellEl) {
                setTimeout(() => {
                    cellEl.classList.add('damage-control-heal');
                    setTimeout(() => cellEl.classList.remove('damage-control-heal'), 1200);
                }, i * 200);
            }
        });
    }

    // Reset panel
    reset(): void {
        if (this.panelElement) {
            const abilityList = this.panelElement.querySelector('.ability-list');
            const passiveList = this.panelElement.querySelector('.passive-list');
            if (abilityList) abilityList.innerHTML = '';
            if (passiveList) {
                passiveList.innerHTML = '';
                const noPassives = document.createElement('div');
                noPassives.className = 'no-passives';
                noPassives.textContent = 'None';
                passiveList.appendChild(noPassives);
            }
        }
        if (this.buffElement) {
            this.buffElement.classList.add('hidden');
        }
        this.hideTargetingMode();
        this.sectorSweepAxis = 'row';

        // Clean up any lingering animation overlays from abilities
        document.querySelectorAll('.scan-grid-overlay, .multi-shot-label, .orbital-hit-count, .scrap-particle, .probe-result-label, .mine-deploy-label').forEach(el => el.remove());
    }
}

export const abilityRenderer = new AbilityRenderer();

// ─── Ability result render registry ──────────────────────────────
//
// Routes server ability results to the renderer methods above, keyed by the
// wire `effect` discriminator. Each effect has up to two perspectives:
//   own      — the caster's view of their own ability
//              (`ability-result` → battlePhase.handleAbilityResult)
//   opponent — the victim's view of the opponent's ability
//              (`opponent-ability` → networkHandlers)
// Adding a new ability effect means adding ONE entry here; the dispatch
// sites call renderOwnAbilityResult / renderOpponentAbility and never change.

/**
 * Shared singletons + current DOM targets the result renderers need,
 * supplied by the dispatch site (which has them in scope already).
 */
export interface AbilityRenderEnv {
    /** #your-board (the player's own fleet board), if mounted. */
    yourBoard: HTMLElement | null;
    /** #enemy-board (the player's tracking board), if mounted. */
    enemyBoard: HTMLElement | null;
    gameState: GameState;
    renderer: Renderer;
    abilityManager: AbilityManager;
}

/** Build an env from the dispatch site's shared singletons + current DOM. */
export function createAbilityRenderEnv(
    deps: Pick<AbilityRenderEnv, 'gameState' | 'renderer' | 'abilityManager'>
): AbilityRenderEnv {
    return {
        yourBoard: document.getElementById('your-board'),
        enemyBoard: document.getElementById('enemy-board'),
        ...deps,
    };
}

interface AbilityEffectRenderer {
    /** Renders the caster's own `ability-result` (success already checked). */
    own?: (result: AbilityResult, env: AbilityRenderEnv) => void;
    /** Renders the victim's `opponent-ability` notification. */
    opponent?: (data: OpponentAbility, env: AbilityRenderEnv) => void;
}

const ABILITY_RESULT_RENDERERS: Record<string, AbilityEffectRenderer> = {
    // area_reveal scan (own) / opponent scanned a cell of our board
    reveal: {
        own: (result, env) => {
            if (result.revealed && env.enemyBoard) {
                abilityRenderer.showRevealedCells(result.revealed, env.enemyBoard);
            }
        },
        opponent: (data, env) => {
            if (env.yourBoard && data.targetX !== undefined && data.targetY !== undefined) {
                abilityRenderer.showOpponentPrecision(data.targetX, data.targetY, env.yourBoard);
            }
        },
    },

    // precision-strike style detection (opponent view only)
    detect: {
        opponent: (data, env) => {
            if (env.yourBoard && data.targetX !== undefined && data.targetY !== undefined) {
                abilityRenderer.showOpponentPrecision(data.targetX, data.targetY, env.yourBoard);
            }
        },
    },

    // multi_shot
    extra_shots: {
        own: (result, env) => {
            abilityRenderer.updateExtraShotsIndicator();
            if (env.enemyBoard) {
                abilityRenderer.showDoubleShotActivation(env.enemyBoard, result.extraShots || 1);
            }
        },
        opponent: (_data, env) => {
            // Power-up effect on your board (where opponent's shots will land)
            if (env.yourBoard) {
                abilityRenderer.showDoubleShotActivation(env.yourBoard);
            }
        },
    },

    sector_sweep: {
        own: (result, env) => {
            if (env.enemyBoard) {
                abilityRenderer.showSectorSweep(result.axis, result.index, result.hasShip, env.enemyBoard);
            }
        },
        opponent: (data, env) => {
            if (env.yourBoard && data.axis !== undefined && data.index !== undefined) {
                abilityRenderer.showSectorSweep(data.axis, data.index, data.hasShip, env.yourBoard);
            }
        },
    },

    repair: {
        own: (result, env) => {
            const { gameState, renderer } = env;
            // Use repairedCells array (supports multi-cell repair), fall back to single cell
            const repCells = result.repairedCells || (result.x !== undefined ? [{ x: result.x, y: result.y! }] : []);
            if (env.yourBoard && repCells.length > 0) {
                for (const rc of repCells) {
                    // Clear the shot from our board state
                    gameState.playerBoard.shots.delete(`${rc.x},${rc.y}`);

                    // Clear from ship's hit tracking
                    const shipId = gameState.playerBoard.grid[rc.y]?.[rc.x];
                    if (shipId) {
                        const ship = gameState.ships.find(s => s.id === shipId);
                        if (ship) {
                            const cellIndex = ship.horizontal ? rc.x - ship.x : rc.y - ship.y;
                            ship.hits.delete(cellIndex);
                        }
                    }

                    abilityRenderer.showRepairEffect(rc.x, rc.y, env.yourBoard);
                }

                // Re-render after animation completes to show healed cells
                setTimeout(() => {
                    renderer.renderYourBoard(gameState.playerBoard);
                    renderer.renderShipsStatus(gameState.ships, 'your-ships-status');
                }, 800);
            }
        },
        opponent: (data, env) => {
            const { gameState, renderer } = env;
            // Clear the shot from our tracking board so we can re-target
            const cells = data.repairedCells || (data.targetX !== undefined ? [{ x: data.targetX, y: data.targetY! }] : []);
            for (const rc of cells) {
                gameState.enemyBoard.clearShot(rc.x, rc.y);
                gameState.enemyShots.delete(`${rc.x},${rc.y}`);
                if (env.enemyBoard) {
                    renderer.clearShotMarker(rc.x, rc.y, env.enemyBoard);
                    renderer.animateRepair(rc.x, rc.y, env.enemyBoard);
                }
            }
            if (cells.length > 1) {
                renderer.showToast(`Opponent repaired ${cells.length} cells!`);
            } else if (cells.length === 1) {
                renderer.showToast(`Opponent repaired cell at ${cellLabel(cells[0].x, cells[0].y)}`);
            }
        },
    },

    // decoy placement renders only for the caster (the opponent must not see it)
    decoy: {
        own: (result, env) => {
            if (env.yourBoard) {
                // Use cells array for multi-cell decoys, or single coord
                const cells = result.cells || [{ x: result.x!, y: result.y! }];
                abilityRenderer.showDecoyPlacement(cells, env.yourBoard);
                // Track decoy locally
                env.abilityManager.addDecoy(result.x!, result.y!, cells);
            }
        },
    },

    scrap_salvo: {
        own: (result, env) => {
            if (result.targets && env.enemyBoard) {
                abilityRenderer.showScrapSalvo(result.targets, env.enemyBoard);
            }
        },
        opponent: (data, env) => {
            if (env.yourBoard && data.targets) {
                abilityRenderer.showScrapSalvo(data.targets, env.yourBoard);
            }
        },
    },

    kamikaze_drone: {
        own: (result, env) => {
            if (result.targetHit && env.enemyBoard) {
                abilityRenderer.showKamikazeDrone(result.targetHit, env.enemyBoard);
            }
            if (result.revealedToEnemy && env.yourBoard) {
                abilityRenderer.showKamikazeDroneReveal(result.revealedToEnemy, env.yourBoard);
            }
        },
        opponent: (data, env) => {
            if (!env.yourBoard) return;
            if (data.targetHit) {
                abilityRenderer.showKamikazeDrone(data.targetHit, env.yourBoard);
            }
            // Reveal penalty shows on enemy board (attacker's board from our perspective)
            if (data.revealedToEnemy && env.enemyBoard) {
                abilityRenderer.showKamikazeDroneReveal(data.revealedToEnemy, env.enemyBoard);
            }
        },
    },

    orbital_bombardment: {
        own: (result, env) => {
            if (env.enemyBoard) {
                abilityRenderer.showOrbitalBombardment(
                    result.axis, result.index,
                    result.hit ? (result.allHitTargets || [result.targetHit!]) : null, env.enemyBoard
                );
            }
        },
        opponent: (data, env) => {
            if (env.yourBoard && data.axis !== undefined && data.index !== undefined) {
                abilityRenderer.showOrbitalBombardment(
                    data.axis, data.index,
                    data.targetHit || null, env.yourBoard
                );
            }
        },
    },

    inquisitors_mandate: {
        own: (result, env) => {
            if (result.revealed && env.enemyBoard) {
                abilityRenderer.showInquisitorMandate(result.revealed, env.enemyBoard);
            }
        },
        opponent: (data, env) => {
            if (env.yourBoard && data.revealed) {
                abilityRenderer.showInquisitorScanned(data.revealed, env.yourBoard);
            }
        },
    },

    probe_network: {
        own: (result, env) => {
            if (result.probePos && env.enemyBoard) {
                abilityRenderer.showProbeDeployed(result.probePos, result.detectionRadius || 3, env.enemyBoard);
            }
        },
        opponent: (data, env) => {
            // Opponent deployed a probe on our board — show probe indicator
            if (env.yourBoard && data.probePos) {
                abilityRenderer.showProbeDeployed(data.probePos, data.detectionRadius || 3, env.yourBoard);
            }
        },
    },

    hunter_killer_mine: {
        own: (result, env) => {
            if (result.minePos && env.enemyBoard) {
                abilityRenderer.showMineDeployed(result.minePos, result.seekRadius, result.enhanced, env.enemyBoard);
            }
        },
        opponent: (data, env) => {
            // Opponent deployed a mine — show mine indicator on our board
            if (env.yourBoard && data.minePos) {
                abilityRenderer.showMineDeployed(data.minePos, data.seekRadius, data.enhanced, env.yourBoard);
            }
        },
    },
};

function lookupEffectRenderer(effect: string | undefined, perspective: 'own' | 'opponent'): AbilityEffectRenderer | null {
    // No effect at all: nothing to render beyond the generic notification
    // the dispatch site already showed (historical default-branch behavior).
    if (!effect) return null;
    const entry = ABILITY_RESULT_RENDERERS[effect];
    if (!entry) {
        // Unknown effect: the generic message toast/notification was already
        // rendered by the caller; just flag the missing registry entry.
        console.warn(`[abilityRenderer] No ${perspective} renderer registered for ability effect "${effect}"`);
        return null;
    }
    return entry;
}

/** Render the caster's own ability result (`ability-result` payload). */
export function renderOwnAbilityResult(result: AbilityResult, env: AbilityRenderEnv): void {
    lookupEffectRenderer(result.effect, 'own')?.own?.(result, env);
}

/** Render the opponent's ability notification (`opponent-ability` payload). */
export function renderOpponentAbility(data: OpponentAbility, env: AbilityRenderEnv): void {
    lookupEffectRenderer(data.effect, 'opponent')?.opponent?.(data, env);
}

// ─── Shot-passive rendering (shared by all shot/ability result paths) ───

/**
 * Which action produced the passive results:
 *   own-action      — our shot/ability landed (shot-result, ability-result):
 *                     offensive passives animate on the enemy tracking board.
 *   opponent-action — the opponent's shot/ability hit us (opponent-shot,
 *                     opponent-ability): everything animates on our board.
 */
export type PassiveRenderPerspective = 'own-action' | 'opponent-action';

/**
 * Render passive-trigger notifications + animations attached to a shot or
 * damage-ability result. Replaces four formerly duplicated if-chains
 * (networkHandlers shot-result / opponent-shot / opponent-ability,
 * battlePhase.handleAbilityResult).
 *
 * @param hit own-action only: the triggering shot's coordinates, used to
 *            stagger the ship_scanner reveal outward from the impact.
 */
export function renderShotPassives(
    passives: PassiveResult[] | undefined,
    perspective: PassiveRenderPerspective,
    env: AbilityRenderEnv,
    hit?: Coord
): void {
    if (!passives) return;

    for (const passive of passives) {
        abilityRenderer.showPassiveTriggered(passive);

        // Splash damage buff grant always pulses the buffed player's own board
        if (passive.buffType === 'splash_damage' && env.yourBoard) {
            abilityRenderer.showSplashDamageActivation(env.yourBoard);
        }

        if (perspective === 'own-action') {
            if (passive.abilityId === 'relentless_assault' && env.enemyBoard) {
                abilityRenderer.showRelentlessAssault(env.enemyBoard);
            }
            if (passive.abilityId === 'ship_scanner' && passive.revealed && passive.revealed.length > 0 && env.enemyBoard) {
                abilityRenderer.showShipScannerReveal(passive.revealed, env.enemyBoard, hit?.x, hit?.y);
            }
            if (passive.abilityId === 'guerrilla_tactics' && env.enemyBoard) {
                abilityRenderer.showGuerrillaTactics(env.enemyBoard);
            }
            if (passive.abilityId === 'iron_might') {
                abilityRenderer.showIronMight();
            }
        } else {
            // Opponent's passives triggering against us render on our board
            if (passive.abilityId === 'relentless_assault' && env.yourBoard) {
                abilityRenderer.showRelentlessAssault(env.yourBoard);
            }
            if (passive.abilityId === 'guerrilla_tactics' && env.yourBoard) {
                abilityRenderer.showGuerrillaTactics(env.yourBoard);
            }
        }
    }
}
